import React, { useState, useRef, useEffect } from 'react';
import {
  FaStar,
  FaQuoteLeft,
  FaArrowLeft,
  FaArrowRight,
  FaUser,
  FaBriefcase,
  FaGraduationCap,
  FaUserTie
} from 'react-icons/fa';

const Testimonials = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const timeoutRef = useRef(null);

  const testimonials = [
    {
      id: 1,
      name: "Meena Patel",
      role: "Parent of Grade 5 Student",
      text: "Excellent co-curricular activities and attention to each child's needs. Highly recommended.",
      rating: 5,
      icon: "👩‍👧",
      iconBg: "bg-blue-100",
      iconColor: "text-blue-600",
      avatarIcon: FaUser
    },
    {
      id: 2,
      name: "Sanjay Gupta",
      role: "Parent of Class 10 Student",
      text: "Outstanding board preparation and career guidance. The teachers were exceptionally supportive.",
      rating: 5,
      icon: "👨‍💼",
      iconBg: "bg-blue-100",
      iconColor: "text-blue-600",
      avatarIcon: FaBriefcase
    },
    {
      id: 3,
      name: "Rahul Sharma",
      role: "Parent of Class 8 Student",
      text: "The teachers are extremely dedicated and the infrastructure is excellent. My child has shown remarkable improvement.",
      rating: 5,
      icon: "👨‍👦",
      iconBg: "bg-blue-100",
      iconColor: "text-blue-600",
      avatarIcon: FaUserTie
    },
    {
      id: 4,
      name: "Priya Verma",
      role: "Alumni (Batch 2020)",
      text: "The foundation I got here helped me excel in my higher studies. Truly grateful for the holistic education.",
      rating: 5,
      icon: "👩‍🎓",
      iconBg: "bg-purple-100",
      iconColor: "text-purple-600",
      avatarIcon: FaGraduationCap
    },
    {
      id: 5,
      name: "Dr. Arvind Singh",
      role: "Parent of LKG Student",
      text: "Safe environment and caring staff. My daughter loves going to school every day.",
      rating: 5,
      icon: "👨‍⚕️",
      iconBg: "bg-blue-100",
      iconColor: "text-blue-600",
      avatarIcon: FaUserTie
    }
  ];

  // Create infinite loop by duplicating items
  const infiniteTestimonials = [...testimonials, ...testimonials, ...testimonials];

  // Check if mobile on resize
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const itemsPerPage = isMobile ? 1 : 3; // Changed from 2 to 3 for desktop
  const totalSlides = testimonials.length;

  // Reset to beginning when reaching the end (infinite effect)
  useEffect(() => {
    if (currentIndex >= totalSlides * 2) {
      // Jump back to middle without animation
      timeoutRef.current = setTimeout(() => {
        setIsTransitioning(false);
        setCurrentIndex(totalSlides);
      }, 300);
    } else if (currentIndex < totalSlides) {
      // Jump to middle without animation
      timeoutRef.current = setTimeout(() => {
        setIsTransitioning(false);
        setCurrentIndex(totalSlides);
      }, 300);
    }
  }, [currentIndex, totalSlides]);

  // Cleanup timeout
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const nextSlide = () => {
    setIsTransitioning(true);
    setCurrentIndex((prev) => prev + 1);
  };

  const prevSlide = () => {
    setIsTransitioning(true);
    setCurrentIndex((prev) => prev - 1);
  };

  const goToSlide = (index) => {
    setIsTransitioning(true);
    setCurrentIndex(index + totalSlides);
  };

  // Calculate transform value
  const getTransformValue = () => {
    const slideWidth = isMobile ? 100 : 33.333; // 100% for mobile, 33.333% for desktop (3 items)
    return `translateX(-${(currentIndex % totalSlides) * slideWidth}%)`;
  };

  // Get visible items for dots
  const getVisibleIndex = () => {
    return currentIndex % totalSlides;
  };

  // Stats data
  const stats = [
    { value: "4.8/5", label: "Satisfaction" },
    { value: "98%", label: "Recommend" },
    { value: "25+", label: "Years" },
    { value: "1000+", label: "Families" }
  ];

  return (
    <section className="py-12 bg-white">
      <div className="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
        {/* Header */}
        <div className="max-w-3xl mx-auto mb-10 text-center">
          <div className="inline-flex items-center px-3 py-1 mb-3 text-xs font-medium rounded-full text-amber-600 bg-amber-50">
            <FaStar className="w-3 h-3 mr-1.5" />
            Testimonials
          </div>
          <h2 className="mb-3 text-2xl font-bold text-gray-900">Voices of Trust</h2>
          <p className="text-sm text-gray-600">Hear from our parents and alumni about their experiences</p>
        </div>

        {/* Carousel Container */}
        <div className="relative px-12 mb-8">
          {/* Previous Button */}
          <button
            onClick={prevSlide}
            className="absolute left-0 top-1/2 -translate-y-1/2 z-10 flex items-center justify-center w-10 h-10 text-gray-600 transition-all duration-300 bg-white border border-gray-300 rounded-full shadow-sm hover:bg-gray-50 hover:border-gray-400 hover:scale-110"
            aria-label="Previous slide"
          >
            <FaArrowLeft className="w-4 h-4" />
          </button>

          {/* Carousel Track */}
          <div className="overflow-hidden">
            <div
              className={`flex ${isTransitioning ? 'transition-transform duration-300 ease-in-out' : ''}`}
              style={{
                transform: getTransformValue(),
              }}
            >
              {infiniteTestimonials.map((testimonial, index) => (
                <div
                  key={`${testimonial.id}-${index}`}
                  className={`flex-shrink-0 px-3 ${
                    isMobile ? 'w-full' : 'w-1/3' // Changed from w-1/2 to w-1/3 for 3 items
                  }`}
                >
                  <div className="h-full p-5 transition-all duration-300 bg-white border border-gray-100 rounded-xl hover:shadow-sm hover:border-gray-200">
                    {/* Quote Icon */}
                    <div className="mb-3">
                      <FaQuoteLeft className="w-6 h-6 text-blue-100" />
                    </div>

                    {/* Rating Stars */}
                    <div className="flex items-center mb-4">
                      {[...Array(testimonial.rating)].map((_, i) => (
                        <FaStar key={i} className="w-4 h-4 mr-1 text-yellow-400" />
                      ))}
                    </div>

                    {/* Testimonial Text */}
                    <p className="mb-6 text-sm italic leading-relaxed text-gray-600">
                      "{testimonial.text}"
                    </p>

                    {/* Author Info */}
                    <div className="flex items-center pt-4 border-t border-gray-100">
                      <div className="mr-3 text-2xl">{testimonial.icon}</div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-gray-900 truncate">{testimonial.name}</div>
                        <div className="text-xs text-gray-500 truncate">{testimonial.role}</div>
                      </div>
                      <div className={`inline-flex items-center justify-center w-8 h-8 rounded-full flex-shrink-0 ${testimonial.iconBg} ${testimonial.iconColor}`}>
                        <testimonial.avatarIcon className="w-4 h-4" />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Next Button */}
          <button
            onClick={nextSlide}
            className="absolute right-0 top-1/2 -translate-y-1/2 z-10 flex items-center justify-center w-10 h-10 text-gray-600 transition-all duration-300 bg-white border border-gray-300 rounded-full shadow-sm hover:bg-gray-50 hover:border-gray-400 hover:scale-110"
            aria-label="Next slide"
          >
            <FaArrowRight className="w-4 h-4" />
          </button>
        </div>

        {/* Dots Navigation */}
        <div className="flex justify-center gap-2 mt-6">
          {testimonials.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`h-2 rounded-full transition-all duration-300 ${
                getVisibleIndex() === index
                  ? 'w-8 bg-amber-500'
                  : 'w-2 bg-gray-300 hover:bg-gray-400'
              }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>

        {/* Stats Section */}
        <div className="p-5 mt-10 border border-gray-100 bg-gradient-to-r from-gray-50 to-white rounded-xl">
          <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-lg font-bold text-gray-900">{stat.value}</div>
                <div className="text-xs text-gray-600">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;